// Copyright (c) 2026 PGNS LLC

import { Link, Outlet } from '@tanstack/react-router';
import { Rss } from 'lucide-react';
import { PgnsLogo } from '@pgns/ui';
import { FEED_URL } from '@/lib/api';
import { DOCS_URL, GITHUB_URL } from '@/lib/env';
import { FallbackBanner } from './fallback-banner';
import { ThemeToggle } from './theme-toggle';

/**
 * Static chrome. Nothing here depends on the status API, which is the point:
 * the shell renders — header, nav, footer — even when the data layer is dark.
 */
export function StatusShell() {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="border-b border-border">
        <div className="mx-auto w-full max-w-3xl px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <PgnsLogo className="h-5 w-auto" />
            <span className="font-mono text-sm">pgns status</span>
          </Link>
          <nav className="ml-auto flex items-center gap-4 text-sm text-muted-foreground">
            <Link
              to="/"
              className="hover:text-foreground"
              activeProps={{ className: 'text-foreground' }}
            >
              Current
            </Link>
            <Link
              to="/incidents"
              className="hover:text-foreground"
              activeProps={{ className: 'text-foreground' }}
            >
              History
            </Link>
            <a href={FEED_URL} className="hover:text-foreground" title="Atom feed">
              <Rss className="size-4" />
              <span className="sr-only">Atom feed</span>
            </a>
            <ThemeToggle />
          </nav>
        </div>
      </header>
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-8">
        <FallbackBanner />
        <Outlet />
      </main>
      <footer className="border-t border-border">
        <div className="mx-auto w-full max-w-3xl px-4 py-6 flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
          <span>
            Probed from three regions outside AWS, so this page survives an outage of what it
            reports on.
          </span>
          <span className="ml-auto flex gap-4">
            <a href={`${DOCS_URL}/reference/status-api`} className="hover:text-foreground">
              Status API
            </a>
            <a href={FEED_URL} className="hover:text-foreground">
              Atom feed
            </a>
            <a href={GITHUB_URL} className="hover:text-foreground">
              GitHub
            </a>
          </span>
        </div>
      </footer>
    </div>
  );
}
